export type MetricFormat = 'currency' | 'currency2dec' | 'integer' | 'percent'

interface BaseMetric {
  label: string
  planned: number
  assortment: number
  format: MetricFormat
}

const BASE_METRICS: BaseMetric[] = [
  {
    label: 'Sales $',
    planned: 292310684,
    assortment: 362114539,
    format: 'currency',
  },
  {
    label: 'Cost $',
    planned: 102575002,
    assortment: 131368805,
    format: 'currency',
  },
  {
    label: 'Sales AUC',
    planned: 1.93,
    assortment: 2.25,
    format: 'currency2dec',
  },
  {
    label: 'Sales AUR',
    planned: 5.5,
    assortment: 6.21,
    format: 'currency2dec',
  },
  {
    label: 'Sales Units',
    planned: 53189687,
    assortment: 58349777,
    format: 'integer',
  },
  {
    label: 'Margin$',
    planned: 189735682,
    assortment: 230745734,
    format: 'currency',
  },
  { label: 'GM%', planned: 64.9, assortment: 63.7, format: 'percent' },
  { label: 'Disc%', planned: 41.0, assortment: 33.4, format: 'percent' },
  {
    label: 'Retail $',
    planned: 489089000,
    assortment: 606464181,
    format: 'currency',
  },
  {
    label: 'Landed Cost $',
    planned: 0,
    assortment: 214969216,
    format: 'currency',
  },
  {
    label: 'FOB Cost $',
    planned: 98923031,
    assortment: 136097339,
    format: 'currency',
  },
  {
    label: 'Units',
    planned: 53672759,
    assortment: 60919697,
    format: 'integer',
  },
  { label: 'Rcpt IMU %', planned: 79.8, assortment: 77.6, format: 'percent' },
  {
    label: 'Rcpt AUR',
    planned: 9.11,
    assortment: 9.96,
    format: 'currency2dec',
  },
]

export interface FinancialsFilterState {
  planId: string | null
  departmentId: string | null
  classId: string | null
  subclassId: string | null
  supplyChainCalendarId: string | null
  seasonCalendarId: string | null
  planStatus: string | null
}

export interface MetricRow {
  label: string
  format: MetricFormat
  planned: number
  assortment: number
  variance: number
  variancePct: number | null
}

function hashString(input: string): number {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function normalizedFromHash(seed: number, salt: string): number {
  // Stable pseudo-random in [0, 1) derived from (seed, salt).
  return (hashString(`${seed}:${salt}`) % 10000) / 10000
}

function shrinkForFilter(filterValue: string, filterName: string): number {
  // Each applied filter narrows scope to roughly 40%–75% of the parent.
  const seed = hashString(filterValue)
  const n = normalizedFromHash(seed, filterName)
  return 0.4 + n * 0.35
}

function activeFilterPairs(
  filters: FinancialsFilterState
): Array<[string, string]> {
  const pairs: Array<[string, string]> = []
  if (filters.planId) pairs.push(['plan', filters.planId])
  if (filters.departmentId) pairs.push(['dept', filters.departmentId])
  if (filters.classId) pairs.push(['class', filters.classId])
  if (filters.subclassId) pairs.push(['subclass', filters.subclassId])
  if (filters.supplyChainCalendarId)
    pairs.push(['supplyCal', filters.supplyChainCalendarId])
  if (filters.seasonCalendarId)
    pairs.push(['seasonCal', filters.seasonCalendarId])
  if (filters.planStatus) pairs.push(['status', filters.planStatus])
  return pairs
}

export function buildFinancialsRows(
  filters: FinancialsFilterState
): MetricRow[] {
  const activePairs = activeFilterPairs(filters)

  // Combined shrink factor for absolute metrics — multiplicative across filters.
  // Hierarchy filters compound (dept → class → subclass each narrow further),
  // and unrelated filters (status, calendars) also reduce the matching slice.
  const totalShrink = activePairs.reduce(
    (acc, [name, value]) => acc * shrinkForFilter(value, name),
    1
  )

  // Seed used for small bidirectional wiggle, distinct per filter combo.
  const wiggleSeed = activePairs.length
    ? hashString(activePairs.map(([n, v]) => `${n}=${v}`).join('|'))
    : 0

  return BASE_METRICS.map((metric, index) => {
    const isAbsolute =
      metric.format === 'currency' || metric.format === 'integer'

    let planned = metric.planned
    let assortment = metric.assortment

    if (wiggleSeed !== 0) {
      if (isAbsolute) {
        // Shrink both columns with the same factor, then add a small per-column
        // noise so planned and assortment don't move in perfect lockstep.
        const plannedNoise =
          (normalizedFromHash(wiggleSeed, `p${index}`) - 0.5) * 0.06 // ±3%
        const assortmentNoise =
          (normalizedFromHash(wiggleSeed, `a${index}`) - 0.5) * 0.06 // ±3%
        planned = planned * totalShrink * (1 + plannedNoise)
        assortment = assortment * totalShrink * (1 + assortmentNoise)
      } else {
        // Ratios (percent, currency2dec unit prices) don't shrink with scope —
        // they just nudge slightly per filter combo.
        const plannedNoise =
          (normalizedFromHash(wiggleSeed, `p${index}`) - 0.5) * 0.1 // ±5%
        const assortmentNoise =
          (normalizedFromHash(wiggleSeed, `a${index}`) - 0.5) * 0.1 // ±5%
        planned = planned * (1 + plannedNoise)
        assortment = assortment * (1 + assortmentNoise)
      }
    }

    if (metric.format === 'percent') {
      planned = clampPercent(planned)
      assortment = clampPercent(assortment)
    }

    const variance = assortment - planned
    const variancePct = planned === 0 ? null : variance / planned

    return {
      label: metric.label,
      format: metric.format,
      planned,
      assortment,
      variance,
      variancePct,
    }
  })
}

function clampPercent(v: number): number {
  if (v < 0) return 0
  if (v > 100) return 100
  return v
}

export function formatMetric(value: number, format: MetricFormat): string {
  switch (format) {
    case 'currency':
      return value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      })
    case 'currency2dec':
      return value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    case 'integer':
      return Math.round(value).toLocaleString('en-US')
    case 'percent':
      return `${value.toFixed(1)}%`
  }
}

export function formatVariance(value: number, format: MetricFormat): string {
  // The variance for a percent metric is a percentage-point delta, not a $ value.
  if (format === 'percent') {
    return `${value.toFixed(1)}%`
  }
  return formatMetric(value, format)
}

export function formatVariancePct(value: number | null): string {
  if (value === null) return '-'
  return `${(value * 100).toFixed(1)}%`
}
